<template>
  <div>
    <a-card style="margin: 20px">
      <template #title>
        <div class="title-box">
          <div class="title-box-left">
            协议专区 > 订单管理 > 创建订单
          </div>
          <div class="title-box-right">
            <a-button @click="goBack">取消</a-button>
          </div>
        </div>
      </template>

      <div class="form-box">
        <!-- 商户协议 -->
        <div class="form-box-item">
          <a-row>
            <a-col :span="4"><span class="required">商户协议</span></a-col>
            <a-col :span="20">
              <PayAgrConfigChooseDialog
                  v-model="formData.merchantName"
                  style="width: 300px;"
                  @chooseDone="onConfigChosen"
              />
            </a-col>
          </a-row>
        </div>

        <!-- 支付类型 -->
        <div class="form-box-item" v-if="currentConfig">
          <a-row>
            <a-col :span="4"><span>支付类型</span></a-col>
            <a-col :span="20">
              <a-radio-group v-model="formData.payType" button-style="solid" disabled>
                <a-radio-button value="PROTOCOL">协议支付</a-radio-button>
                <a-radio-button value="BALANCE">余额支付（预留）</a-radio-button>
              </a-radio-group>
            </a-col>
          </a-row>
        </div>

        <!-- 付款方 -->
        <div class="form-box-item" v-if="currentConfig">
          <a-row>
            <a-col :span="4"><span class="required">付款方</span></a-col>
            <a-col :span="20">
              <div class="payment-box">
                <a-descriptions :column="1" bordered size="small">
                  <a-descriptions-item label="付款户名">{{ currentConfig.payerName }}</a-descriptions-item>
                  <a-descriptions-item label="付款账号">{{ currentConfig.payerAccount }}</a-descriptions-item>
                </a-descriptions>
              </div>
              <div class="field-tip">付款方由商户协议确定，不可修改</div>
            </a-col>
          </a-row>
        </div>

        <!-- 收款方 -->
        <div class="form-box-item" v-if="currentConfig">
          <a-row>
            <a-col :span="4"><span class="required">收款方</span></a-col>
            <a-col :span="20">
              <div class="payment-box">
                <a-descriptions :column="1" bordered size="small">
                  <a-descriptions-item label="收款户名">{{ currentConfig.payeeName }}</a-descriptions-item>
                  <a-descriptions-item label="收款账号">{{ currentConfig.payeeAccount }}</a-descriptions-item>
                  <a-descriptions-item label="开户银行">{{ currentConfig.payeeBankName || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="开户行号">{{ currentConfig.payeeBankNo || '-' }}</a-descriptions-item>
                </a-descriptions>
              </div>
            </a-col>
          </a-row>
        </div>

        <!-- 订单总金额 -->
        <div class="form-box-item">
          <a-row>
            <a-col :span="4"><span class="required">订单总金额</span></a-col>
            <a-col :span="20">
              <a-input-number
                  v-model="formData.totalAmount"
                  :min="0.01"
                  :precision="2"
                  :step="100"
                  placeholder="请输入订单总金额（元）"
                  style="width: 300px;"
                  @change="onAmountChange"
              />
            </a-col>
          </a-row>
        </div>

        <!-- 支付摘要 -->
        <div class="form-box-item">
          <a-row>
            <a-col :span="4"><span>支付摘要</span></a-col>
            <a-col :span="20">
              <a-input
                  v-model.trim="formData.paySummary"
                  allow-clear
                  placeholder="银行转账附言，留空则用配置默认摘要"
                  style="width: 300px;"
              />
            </a-col>
          </a-row>
        </div>

        <!-- 备注 -->
        <div class="form-box-item">
          <a-row>
            <a-col :span="4"><span>备注</span></a-col>
            <a-col :span="20">
              <a-input
                  v-model.trim="formData.remark"
                  allow-clear
                  placeholder="选填"
                  style="width: 300px;"
              />
            </a-col>
          </a-row>
        </div>

        <!-- 金额预览 -->
        <div class="form-box-item" v-if="preview">
          <a-row>
            <a-col :span="4"><span>金额预览</span></a-col>
            <a-col :span="20">
              <div class="preview-box">
                <a-alert type="info" show-icon style="margin-bottom: 12px;"
                         message="服务费 = 比例 × 总额 或 固定金额（四舍五入保留2位）；到账金额 = 总额 − 服务费合计。"/>
                <a-descriptions :column="3" bordered size="small" style="margin-bottom: 12px;">
                  <a-descriptions-item label="订单总金额">
                    <Balance :amount="preview.totalAmount" prefix="￥"/>
                  </a-descriptions-item>
                  <a-descriptions-item label="服务费合计">
                    <Balance :amount="preview.feeTotalAmount" prefix="￥"/>
                  </a-descriptions-item>
                  <a-descriptions-item label="收款到账">
                    <span style="color: #52c41a; font-weight: 600;">
                      <Balance :amount="preview.payeeAmount" prefix="￥"/>
                    </span>
                  </a-descriptions-item>
                </a-descriptions>
                <a-table
                    v-if="preview.feeDetails && preview.feeDetails.length"
                    :columns="feeDetailColumns"
                    :data-source="preview.feeDetails"
                    :pagination="false"
                    :row-key="(record, index) => index"
                    size="small"
                >
                  <template slot="feeType" slot-scope="text">
                    {{ getDictLabel(payAgrDict.feeType, text) }}
                  </template>
                  <template slot="feeValue" slot-scope="text, record">
                    <span v-if="record.feeType === 1">{{ text }}%</span>
                    <span v-else>{{ text }} 元</span>
                  </template>
                  <template slot="feeAmount" slot-scope="text">
                    <Balance :amount="text" prefix="￥"/>
                  </template>
                </a-table>
                <div v-else style="color: #999;">该商户协议未配置服务费，订单总金额将全额到账收款方</div>
              </div>
            </a-col>
          </a-row>
        </div>

        <!-- 短信验证码 -->
        <div class="form-box-item" v-if="preview">
          <a-row>
            <a-col :span="4"><span class="required">短信验证码</span></a-col>
            <a-col :span="20">
              <PaymentSms
                  v-if="currentConfig && payerMobile"
                  :verify-code.sync="formData.verifyCode"
                  :account-name="currentConfig.payerName"
                  :account-no="currentConfig.payerAccount"
                  :channel-no="currentConfig.channelNo"
                  :app-no="currentConfig.appNo"
                  :pay-amount="preview ? preview.totalAmount : 0"
                  model-no="102216"
                  :mobile="payerMobile"
                  style="width: 300px;"
              />
              <a-alert
                  v-else-if="!payerMobile"
                  type="warning"
                  show-icon
                  style="width: 300px;"
                  message="付款方账户未登记手机号，无法发送验证码，请联系管理员补充"
              />
              <div class="field-tip">验证码将发送至付款方预留手机 <strong>{{ payerMobile }}</strong>，验证通过后将立即创建订单并执行划付</div>
            </a-col>
          </a-row>
        </div>

        <!-- 提交按钮 -->
        <div class="form-box-item">
          <a-row>
            <a-col :span="4"></a-col>
            <a-col :span="20">
              <div style="display: flex; flex-direction: column; align-items: flex-start;">
                <a-tooltip :title="disabledReason" placement="top">
                  <a-button
                      :disabled="!canSubmit || confirmLoading"
                      :loading="confirmLoading"
                      icon="check-circle"
                      style="width: 300px;"
                      type="primary"
                      @click="handleSubmit"
                  >
                    创建并执行
                  </a-button>
                </a-tooltip>
                <a-button
                    icon="clock-circle"
                    style="width: 300px; margin-top: 8px;"
                    type="link"
                    @click="goBack"
                >
                  历史订单
                </a-button>
              </div>
            </a-col>
          </a-row>
        </div>
      </div>
    </a-card>
  </div>
</template>

<script>
import {message} from "ant-design-vue";
import Balance from "@/view/components/Balance.vue";
import PaymentSms from "@/view/components/PaymentSms.vue";
import PayAgrConfigChooseDialog from "@/view/components/ChooseDialog/PayAgrConfigChooseDialog.vue";
import {payAgrDict, getDictLabel} from "@/utils/bizDict/payAgrDict";

export default {
  name: 'PayAgrOrderCreate',
  components: {Balance, PaymentSms, PayAgrConfigChooseDialog},
  data() {
    return {
      payAgrDict,
      currentConfig: null,
      preview: null,
      previewTimer: null,
      confirmLoading: false,
      payerMobile: '',
      firstActivated: true,
      formData: {
        merchantNo: undefined,
        merchantName: '',
        payerAccount: '',
        payerName: '',
        payType: 'PROTOCOL',
        totalAmount: null,
        paySummary: '',
        remark: '',
        verifyCode: '',
      },
      feeDetailColumns: [
        {title: '收款账户', dataIndex: 'feeAccount', align: 'center'},
        {title: '户名', dataIndex: 'feeName', align: 'center'},
        {title: '类型', dataIndex: 'feeType', align: 'center', width: 90, scopedSlots: {customRender: 'feeType'}},
        {title: '费率/金额', dataIndex: 'feeValue', align: 'center', width: 100, scopedSlots: {customRender: 'feeValue'}},
        {title: '服务费', dataIndex: 'feeAmount', align: 'center', width: 120, scopedSlots: {customRender: 'feeAmount'}},
      ],
    };
  },
  computed: {
    canSubmit() {
      return !!(this.currentConfig &&
          this.formData.totalAmount && Number(this.formData.totalAmount) > 0 &&
          this.preview &&
          this.payerMobile &&
          this.formData.verifyCode);
    },
    /** 按钮禁用原因（用于 tooltip 提示） */
    disabledReason() {
      if (this.confirmLoading) return '提交中...';
      if (!this.currentConfig) return '请先选择商户协议';
      if (!this.formData.totalAmount || Number(this.formData.totalAmount) <= 0) return '请输入订单总金额';
      if (!this.preview) return '金额试算中或试算失败，请稍候';
      if (!this.payerMobile) return '付款方账户未登记手机号，无法发送验证码';
      if (!this.formData.verifyCode) return '请填写短信验证码';
      return '';
    },
  },
  activated() {
    // 菜单入口进入时本页会被 keep-alive 缓存，重新进入需还原为全新表单
    if (this.firstActivated) {
      this.firstActivated = false;
      return;
    }
    this.resetForm();
  },
  beforeDestroy() {
    if (this.previewTimer) {
      clearTimeout(this.previewTimer);
      this.previewTimer = null;
    }
  },
  methods: {
    getDictLabel,

    resetForm() {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
        this.previewTimer = null;
      }
      this.currentConfig = null;
      this.preview = null;
      this.payerMobile = '';
      this.confirmLoading = false;
      this.formData = {
        merchantNo: undefined,
        merchantName: '',
        payerAccount: '',
        payerName: '',
        payType: 'PROTOCOL',
        totalAmount: null,
        paySummary: '',
        remark: '',
        verifyCode: '',
      };
    },

    async onConfigChosen(config) {
      this.currentConfig = config;
      this.formData.merchantNo = config.merchantNo;
      this.formData.payerAccount = config.payerAccount;
      this.formData.payerName = config.payerName;
      this.formData.paySummary = config.defaultPaySummary || '';
      this.preview = null;
      this.payerMobile = '';
      this.formData.verifyCode = '';
      // 实时从 CustAccountInfo 查询付款方手机号（不做冗余落库）
      try {
        const res = await this.api.accountqueryBalancedApi({
          channel_no: config.channelNo,
          account_no: config.payerAccount,
        });
        if (res.code === 200 && res.data) {
          this.payerMobile = res.data.mobile || '';
        }
      } catch (e) {
        console.error('查询付款方手机号失败:', e);
      }
      this.tryPreview();
    },

    onAmountChange() {
      this.tryPreview();
    },

    /** 防抖试算（No-Loading，避免遮罩闪烁） */
    tryPreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer);
      if (!this.formData.merchantNo || !this.formData.totalAmount || Number(this.formData.totalAmount) <= 0) {
        this.preview = null;
        return;
      }
      this.previewTimer = setTimeout(async () => {
        try {
          const res = await this.api.previewPayAgrOrder({
            merchantNo: this.formData.merchantNo,
            totalAmount: this.formData.totalAmount,
          });
          if (res.code === 200 && res.data) {
            this.preview = res.data;
          } else {
            this.preview = null;
            message.warning(res.info || '金额试算失败');
          }
        } catch (error) {
          console.error('金额试算失败:', error);
          this.preview = null;
        }
      }, 400);
    },

    handleSubmit() {
      if (!this.currentConfig) {
        return message.error('请先选择商户协议');
      }
      if (!this.formData.totalAmount || Number(this.formData.totalAmount) <= 0) {
        return message.error('请输入订单总金额');
      }
      if (!this.preview) {
        return message.warning('请先完成金额试算');
      }
      if (!this.payerMobile) {
        return message.warning('付款方账户未登记手机号，无法发送验证码，请联系管理员补充');
      }
      if (!this.formData.verifyCode || this.formData.verifyCode.length < 4) {
        return message.warning('请输入短信验证码');
      }
      this.$confirm({
        title: '确认创建并执行',
        content: `订单总金额 ${this.preview.totalAmount} 元，服务费合计 ${this.preview.feeTotalAmount} 元，收款到账 ${this.preview.payeeAmount} 元。确认后将立即发起付款，请核对以上金额。`,
        onOk: async () => {
          this.confirmLoading = true;
          try {
            const res = await this.api.createPayAgrOrder({...this.formData});
            if (res.code === 200 && res.data) {
              const data = res.data;
              this.$success({
                title: '订单已提交执行',
                content: `订单号：${data.orderNo || data.records?.[0]?.orderNo || '-'}，当前状态：${this.getStatusLabel(data.status)}`,
                onOk: () => this.$router.push('/pay-agr/order'),
              });
            } else {
              message.error(res.info || '创建订单失败');
            }
          } catch (error) {
            console.error('创建订单失败:', error);
            message.error('创建订单失败');
          } finally {
            this.confirmLoading = false;
          }
        },
      });
    },

    getStatusLabel(status) {
      const map = {0: '待执行', 1: '待执行', 2: '处理中', 3: '完成', 4: '服务费失败', 5: '支付失败'};
      return map[status] || status || '-';
    },

    goBack() {
      this.$router.push('/pay-agr/order');
    },
  },
};
</script>

<style lang="less" scoped>
.title-box {
  display: flex;
  justify-content: space-between;
}

.form-box {
  padding: 50px;

  .form-box-item {
    margin-bottom: 20px;

    .ant-input {
      max-width: 300px;
    }

    .payment-box {
      width: 450px;
      margin-top: 10px;
    }

    .preview-box {
      max-width: 720px;
    }

    .required::before {
      content: '*';
      color: #ff4d4f;
      margin-right: 4px;
    }
  }
}

.field-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
  line-height: 1.6;
}
</style>
